/**
 * UseAICatalog — shared react-query fetch of `GET /ai/catalog`.
 *
 * The catalog is the server-side source of truth for the AI's user-facing
 * surface: tools (palette cards), skills, and scenarios (the canned-prompt
 * registry — see COMS scenarios/Scenario.ts). Extracted from useAICommands so
 * AIReviewButton and the palette read the same cached response (one fetch per
 * session, 5-minute staleTime under the shared ['aiCatalog'] key).
 */
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';

export interface CatalogTool {
  name: string;
  description: string;
  category: string;
  kind: 'data_source' | 'ics_read' | 'ics_write';
  requiresConfirmation: boolean;
  exampleInvocation?: string;
  imhReference?: string;
  virtualOf?: string;
  /** User-facing data sources (picker labels: "Incident", "Files", "Web", …). */
  dataSources?: string[];
  /** Feature flag that must be active for the palette to surface this card. */
  requiredFeatureFlag?: string;
  /**
   * Feature flag that, when active, hides this card — the server removes the
   * tool from the agent for such groups (e.g. web grounding under
   * HIGH_SECURITY).
   */
  blockedByFeatureFlag?: string;
}

/** Guided-workflow subagent (SkillRegistry metadata) served alongside tools. */
export interface CatalogSkill {
  name: string;
  description: string;
  category: string;
  toolNames: string[];
  examplePrompts: string[];
  entryConditions?: string;
  imhReference?: string;
  /** Union of the composed tools' data sources, derived server-side. */
  dataSources?: string[];
  /**
   * Feature flag that must be active for the palette to surface this skill. The
   * server independently enforces the same requirement when building the agent,
   * so an unflagged client can never dispatch a gated skill.
   */
  requiredFeatureFlag?: string;
  /**
   * Feature flag that, when active, hides this skill — the server removes it
   * from the agent for such groups (e.g. web_research under HIGH_SECURITY).
   */
  blockedByFeatureFlag?: string;
}

/**
 * One concrete illustration: a circumstance and the sample response the agent
 * gives in it. Illustrative, not a recording — doubles as the manual QA case.
 */
export interface ScenarioExample {
  situation: string;
  /** Curated sample response, markdown, in the agent's real output style. */
  response: string;
}

/**
 * Canned-prompt registry entry served in the catalog response (COMS
 * CatalogScenario): a ready prompt + description + guidance + concrete example
 * responses. `ui` is present only on the one-click actions (review buttons).
 */
export interface CatalogScenario {
  name: string;
  /** Bumped server-side whenever the prompt or examples change. */
  version: number;
  title: string;
  /** One-sentence "what this command is for". */
  description: string;
  /** Plain-language "when and why to use it". */
  guidance: string;
  /** Grouping key, e.g. 'review'. */
  category: string;
  mode: 'ask' | 'agent_confirm' | 'agent_auto';
  target:
    | { kind: 'tool'; toolName: string }
    | { kind: 'skill'; skillName: string }
    | { kind: 'agent' };
  /**
   * Descriptive data-binding metadata, e.g. { entity: 'objectives', scope:
   * 'incident' }. Drives palette grouping only — not a stored binding: the data
   * an invocation reads is resolved server-side per message from the request's
   * current incident/group context, so switching incidents never leaves a stale
   * binding.
   */
  context?: { entity: string; scope: 'incident' | 'group' | 'global' };
  /** User-facing data sources (picker labels: "Incident", "Files", "Web", …). */
  dataSources?: string[];
  /** The full canned prompt prefilled into the chat input. */
  prompt: string;
  examples: ScenarioExample[];
  ui?: {
    buttonLabel: string;
    chatName: string;
    command: string;
  };
  /** Feature flag that must be active for the palette to surface this scenario. */
  requiredFeatureFlag?: string;
}

export interface AICatalog {
  tools: CatalogTool[];
  skills: CatalogSkill[];
  /**
   * Optional so an older COMS (pre-scenario registry) still type-checks against
   * this shape.
   */
  scenarios?: CatalogScenario[];
  /** The active chat model's context limits (drives the chat context meter). */
  model?: CatalogModel;
}

export interface CatalogModel {
  name: string;
  maxInputTokens: number;
  /** Context size at which a chat is compacted automatically. */
  compactTriggerTokens: number;
  clearTriggerTokens: number;
}

export const aiCatalogKeys = {
  base: ['aiCatalog'] as const,
};

export const useAICatalog = () =>
  useQuery<AICatalog>({
    queryKey: aiCatalogKeys.base,
    queryFn: async () => {
      const response = await axios.get(config.apiGateway.aiCatalog);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

/**
 * Look up one scenario by name from the shared catalog cache. `scenario` is
 * undefined while loading/failed.
 */
export const useAIScenario = (scenarioName: string) => {
  const { data, isLoading, isError } = useAICatalog();
  return {
    scenario: data?.scenarios?.find((s) => s.name === scenarioName),
    isLoading,
    isError,
  };
};
